import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight, Check, X, PlayCircle, CalendarClock, ShieldCheck,
  FileX, Milestone, ImageOff, ThumbsDown, MessagesSquare, Frown,
  FileQuestion, EyeOff, Users, History, Wallet, Activity, CheckCheck,
  FolderCheck, FileText, BadgeDollarSign, CalendarDays, BadgeCheck, Eye, Scale, CreditCard,
} from "lucide-react";
import { goToAnchor } from "@/lib/nav";
import PhoneMock from "@/components/PhoneMock";

const ICONS = {
  FileX, Milestone, ImageOff, ThumbsDown, MessagesSquare, Frown, FileQuestion, EyeOff,
  Users, History, Wallet, Activity, CheckCheck, FolderCheck, CalendarClock, FileText,
  BadgeDollarSign, CalendarDays, BadgeCheck, Eye, Scale, CreditCard, ShieldCheck,
};

export const WhyIcon = ({ name, className }) => {
  const Icon = ICONS[name] || Check;
  return <Icon className={className} />;
};

export const usePageMeta = (title, desc) => {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", desc);
  }, [title, desc]);
};

export const useGo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return {
    join: () => goToAnchor(navigate, pathname, "#early-access"),
    seeHow: () => goToAnchor(navigate, pathname, "#how-it-works"),
    route: (to) => navigate(to),
  };
};

export const Reveal = ({ children, delay = 0, className = "", y = 26 }) => {
  if (typeof window !== "undefined" && window.__WP_STATIC__ === true) {
    return <div className={className}>{children}</div>;
  }
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const Heading = ({ overline, title, subtitle, center = true }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    {overline && (
      <span className="inline-block text-xs tracking-[0.18em] uppercase text-[#16a34a] font-bold mb-4">{overline}</span>
    )}
    <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] leading-[1.1] font-extrabold tracking-tight text-[#091630]">
      {title}
    </h2>
    {subtitle && <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">{subtitle}</p>}
  </div>
);

export const WhyHero = ({ eyebrow, title, subtitle, primaryLabel = "Join Early Access", secondaryLabel, visual, primaryHow = false }) => {
  const go = useGo();
  return (
    <section data-testid="why-hero" className="relative overflow-hidden pt-28 pb-14 sm:pt-32 sm:pb-20">
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#ecfdf3] blur-3xl opacity-80" />
      <div className="absolute -top-20 right-0 h-96 w-96 rounded-full bg-[#ecfeff] blur-3xl opacity-70" />
      <div className={`relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid ${visual ? "lg:grid-cols-2" : ""} gap-12 items-center`}>
        <Reveal>
          <div className={visual ? "" : "max-w-3xl mx-auto text-center"}>
            <span className="inline-block text-xs tracking-[0.2em] uppercase text-[#16a34a] font-bold mb-4">{eyebrow}</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight text-[#091630] leading-[1.04]">
              {title}
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-xl">{subtitle}</p>
            <div className={`mt-8 flex flex-col sm:flex-row gap-3 ${visual ? "" : "justify-center"}`}>
              <button
                data-testid="why-hero-primary"
                onClick={primaryHow ? go.seeHow : go.join}
                className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors flex items-center justify-center gap-2 green-shadow"
              >
                {primaryLabel} <ArrowRight className="h-5 w-5" />
              </button>
              {secondaryLabel && (
                <button
                  data-testid="why-hero-secondary"
                  onClick={go.seeHow}
                  className="bg-white text-[#091630] font-semibold py-3.5 px-7 rounded-full border border-slate-200 hover:border-slate-300 transition-colors flex items-center justify-center gap-2 soft-shadow"
                >
                  <PlayCircle className="h-5 w-5 text-[#16a34a]" /> {secondaryLabel}
                </button>
              )}
            </div>
          </div>
        </Reveal>
        {visual && <Reveal delay={0.12}>{visual}</Reveal>}
      </div>
    </section>
  );
};

export const PainGrid = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {items.map((p, i) => (
      <Reveal key={p.title} delay={(i % 3) * 0.06}>
        <div data-testid={`pain-${i}`} className="h-full bg-white rounded-2xl border border-slate-100 soft-shadow p-6">
          <div className="h-11 w-11 rounded-xl bg-[#fef2f2] flex items-center justify-center mb-4">
            <WhyIcon name={p.icon} className="h-5 w-5 text-rose-500" />
          </div>
          <h3 className="font-display text-base font-bold text-[#091630] mb-1.5">{p.title}</h3>
          <p className="text-sm text-slate-500 leading-relaxed">{p.text}</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const FlowSequence = ({ steps }) => (
  <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
    {steps.map((s, i) => (
      <React.Fragment key={s}>
        <div className="flex items-center gap-2 bg-white rounded-full border border-slate-100 soft-shadow px-4 py-2.5">
          <span className="h-6 w-6 rounded-full bg-[#ecfdf3] text-[#16a34a] text-xs font-bold flex items-center justify-center">
            {i + 1}
          </span>
          <span className="text-sm font-semibold text-[#091630]">{s}</span>
        </div>
        {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-[#21c55e]" />}
      </React.Fragment>
    ))}
  </div>
);

export const CompareTable = ({ colA, colB, rows }) => (
  <div className="mt-12 max-w-4xl mx-auto rounded-3xl border border-slate-100 soft-shadow bg-white overflow-hidden">
    <div className="grid grid-cols-3 bg-[#091630] text-white">
      <div className="p-4 sm:p-5 text-xs sm:text-sm font-semibold text-slate-300">Feature</div>
      <div className="p-4 sm:p-5 text-xs sm:text-sm font-bold border-l border-white/10">{colA}</div>
      <div className="p-4 sm:p-5 text-xs sm:text-sm font-bold border-l border-white/10 bg-[#16a34a]/20 text-[#21c55e]">{colB}</div>
    </div>
    {rows.map((r, i) => (
      <div key={r.label} className={`grid grid-cols-3 ${i % 2 ? "bg-[#f7faf8]" : "bg-white"}`}>
        <div className="p-4 sm:p-5 text-xs sm:text-sm font-bold text-[#091630]">{r.label}</div>
        <div className="p-4 sm:p-5 text-xs sm:text-sm text-slate-500 border-l border-slate-100 flex items-start gap-2">
          <X className="h-4 w-4 text-slate-300 shrink-0 mt-0.5" /> {r.a}
        </div>
        <div className="p-4 sm:p-5 text-xs sm:text-sm text-[#091630] font-medium border-l border-slate-100 bg-[#ecfdf3]/40 flex items-start gap-2">
          <Check className="h-4 w-4 text-[#16a34a] shrink-0 mt-0.5" /> {r.b}
        </div>
      </div>
    ))}
  </div>
);

export const QuoteStrip = ({ quotes }) => (
  <div className="mt-10 flex flex-wrap justify-center gap-3">
    {quotes.map((q, i) => (
      <Reveal key={q} delay={(i % 5) * 0.05}>
        <div className="bg-white rounded-2xl border border-slate-100 soft-shadow px-5 py-3.5">
          <p className="text-sm sm:text-base font-semibold text-[#091630] italic">“{q}”</p>
        </div>
      </Reveal>
    ))}
  </div>
);

export const CheckList = ({ items, title }) => (
  <div className="bg-white rounded-3xl border-2 border-[#21c55e]/30 soft-shadow p-7 sm:p-8">
    {title && <h3 className="font-display text-lg font-bold text-[#091630] mb-5">{title}</h3>}
    <ul className="space-y-3.5">
      {items.map((it) => (
        <li key={it} className="flex items-center gap-3 text-[15px] text-[#091630] font-medium">
          <span className="h-5 w-5 rounded-full bg-[#ecfdf3] flex items-center justify-center shrink-0">
            <Check className="h-3 w-3 text-[#16a34a]" />
          </span>
          {it}
        </li>
      ))}
    </ul>
  </div>
);

export const IconCardGrid = ({ items }) => (
  <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {items.map((c, i) => (
      <Reveal key={c.title} delay={(i % 3) * 0.06}>
        <div data-testid={`icard-${i}`} className="h-full bg-white rounded-2xl border border-slate-100 soft-shadow p-6 flex items-start gap-4">
          <div className="h-11 w-11 rounded-xl bg-[#ecfdf3] flex items-center justify-center shrink-0">
            <WhyIcon name={c.icon} className="h-5 w-5 text-[#16a34a]" />
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-[#091630] leading-tight">{c.title}</h3>
            {c.text && <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{c.text}</p>}
          </div>
        </div>
      </Reveal>
    ))}
  </div>
);

export const LabelChips = ({ items }) => (
  <div className="mt-12 flex flex-wrap justify-center gap-3 max-w-3xl mx-auto">
    {items.map((c, i) => (
      <Reveal key={c.label} delay={(i % 4) * 0.05}>
        <div className="flex items-center gap-2.5 bg-white rounded-2xl border border-slate-100 soft-shadow px-5 py-3">
          <WhyIcon name={c.icon} className="h-5 w-5 text-[#16a34a]" />
          <span className="text-sm font-semibold text-[#091630]">{c.label}</span>
        </div>
      </Reveal>
    ))}
  </div>
);

export const WhyFinalCTA = ({ title, subtitle, primaryLabel = "Join Early Access", secondaryLabel = "Book a Demo", primaryHow = false }) => {
  const go = useGo();
  return (
    <section data-testid="why-final-cta" className="relative py-20 sm:py-28 bg-[#091630] overflow-hidden">
      <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#06b6d4]/15 blur-3xl" />
      <div className="absolute inset-0 dotted-bg opacity-[0.15]" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">{title}</h2>
          {subtitle && <p className="mt-5 text-lg text-slate-300 max-w-xl mx-auto">{subtitle}</p>}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="why-cta-primary"
              onClick={primaryHow ? go.seeHow : go.join}
              className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2"
            >
              {primaryLabel} <ArrowRight className="h-5 w-5" />
            </button>
            {secondaryLabel && (
              <button
                data-testid="why-cta-secondary"
                onClick={go.join}
                className="bg-transparent text-white font-semibold py-3.5 px-7 rounded-full border border-white/25 hover:bg-white/10 transition-colors"
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------------- Product UI mockups → realistic half-iPhone screens ---------------- */

export const PaymentPageMock = () => <PhoneMock variant="payment" />;

export const MilestoneTrackerMock = () => <PhoneMock variant="milestone" />;

export const PayoutMock = () => <PhoneMock variant="payout" />;
